// ---------------------------------------------------------
// Datei: maintenance.ts
// Zweck: Gemeinsame Datenverträge für wiederkehrende Serviceprotokolle.
// Kontext: Entwürfe und finale Nachweise sind projektbezogen und offline queuebar.
// Stand: 2026-07-29
// ---------------------------------------------------------

export type MaintenanceChecklistStatus = 'ok' | 'not_ok' | 'not_applicable';

export interface MaintenanceChecklistItem {
  id: string;
  label: string;
  status: MaintenanceChecklistStatus;
  comment?: string;
}

export interface MaintenanceMeasurements {
  outdoorUnitSerialNumber?: string;
  compressorOperatingHours?: number;
  auxiliaryHeaterOperatingHours?: number;
  systemPressureBar?: number;
  energyYieldHeatingKwh?: number;
  energyYieldHotWaterKwh?: number;
  copHeating?: number;
  copHotWater?: number;
  totalElectricityConsumptionKwh?: number;
  electricalComponentsReplaced: boolean;
  statisticsReset: boolean;
}

/** Projektdaten, die mit dem finalen Serviceprotokoll als PDF-Momentaufnahme ausgegeben werden. */
export interface MaintenanceProtocolProjectDetails {
  projectName?: string;
  contactName?: string;
  contactPhone?: string;
  contactMobilePhone?: string;
  contactEmail?: string;
  address?: string;
  zip?: string;
  city?: string;
}

export interface MaintenanceAttachment {
  fileId: string;
  fileName: string;
  webViewLink?: string;
  thumbnailLink?: string;
  contentType?: string;
  uploadedAtUtc: string;
}

export interface MaintenanceProtocol {
  id: string;
  dealId: string;
  maintenanceDate: string;
  status: 'draft' | 'finalized';
  checklist: MaintenanceChecklistItem[];
  measurements: MaintenanceMeasurements;
  generalComment?: string;
  employeeSignatureDataUrl?: string;
  customerSignatureDataUrl?: string;
  attachments: MaintenanceAttachment[];
  driveFolderId?: string;
  createdByEmail: string;
  finalizedByEmail?: string;
  createdAtUtc: string;
  updatedAtUtc: string;
  finalizedAtUtc?: string;
}

export interface MaintenanceProtocolUpsertPayload {
  maintenanceDate: string;
  checklist: MaintenanceChecklistItem[];
  measurements: MaintenanceMeasurements;
  generalComment?: string;
  employeeSignatureDataUrl?: string;
  customerSignatureDataUrl?: string;
}

export const maintenanceChecklistDefaults = (): MaintenanceChecklistItem[] => [
  ['heating-circuit-leak-check', 'Sichtprüfung auf Undichtigkeiten im Heizkreis durchgeführt'],
  ['connection-insulation-check', 'Wärmeschutzisolierung der Anschlussleitungen zur Außeneinheit geprüft'],
  ['electrical-connections-check', 'Elektrische Anschlüsse auf festen Sitz geprüft und Verunreinigungen entfernt'],
  ['controller-parameters-check', 'Einstellparameter am Regler kontrolliert'],
  ['compressor-hours-recorded', 'Betriebsstunden für den Kompressor notiert'],
  ['auxiliary-heater-hours-recorded', 'Betriebsstunden für die Elektro-Zusatzheizung notiert'],
  ['energy-yield-recorded', 'Energieertrag ausgelesen und dokumentiert'],
  ['actuator-test', 'Komponententest aller angeschlossenen Aktoren durchgeführt'],
  ['operating-modes-check', 'Funktion aller Betriebsarten geprüft'],
  ['heating-filter-check', 'Schmutzfilter für Heizung geprüft'],
  ['outdoor-heat-exchanger-check', 'Wärmetauscher der Außeneinheit auf Verschmutzung kontrolliert'],
  ['rubber-feet-check', 'Gummifüße auf Verschleiß geprüft'],
  ['condensate-drain-check', 'Kondenswasserablauf geprüft'],
  ['refrigerant-leak-test', 'Dichtheitsprüfung Kältekreis durchgeführt'],
  ['brine-compressor-phase-current-check', 'Stromaufnahme der Phasen am Kompressor im Kältekreis geprüft (bei Sole-Anlagen)'],
  ['r290-installation-check', 'Aufstellungsbedingungen außen geprüft (Schutzzone, nur R290)'],
  ['vde-0701-test', 'Prüfung nach VDE 0701 durchgeführt (bei getauschten elektrischen Bauteilen)'],
  ['safety-valve-leak-check', 'Sichtprüfung auf Undichtigkeiten an Ausdehnungsgefäßen/Sicherheitsventilen durchgeführt'],
  ['safety-valve-function-check', 'Funktion der Sicherheitsventile geprüft'],
  ['expansion-vessel-pressure-check', 'Druckpolster geprüft und gegebenenfalls wiederhergestellt'],
  ['fill-and-vent-check', 'Wasser aufgefüllt und Anlage entlüftet'],
  ['controller-settings-corrected', 'Reglereinstellungen geprüft und gegebenenfalls korrigiert'],
  ['magnesium-anode-check', 'Magnesiumschutzanode geprüft und gegebenenfalls ausgetauscht'],
  ['hot-water-tank-cleaned', 'Warmwasserspeicher gereinigt'],
].map(([id, label]) => ({ id, label, status: 'not_applicable' }));

export const emptyMaintenanceMeasurements = (): MaintenanceMeasurements => ({
  electricalComponentsReplaced: false,
  statisticsReset: false,
});
